import React, { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { QRCodeSVG } from "qrcode.react";
import { PUBLIC_FRONT_URL } from "../lib/publicFront";

// Asset sticker print sheet.
//
// Layout: A4 portrait, 2 columns × 5 rows = 10 stickers per page = one pack.
// Each sticker measures 80mm × 50mm (matches the main card sleeve format
// so a homeowner can keep them in the same wallet pocket). Black border,
// big QR code, asset code under the QR, Curb Crew branding bottom-right.
//
// Print settings (browser dialog):
//   • Paper: A4, portrait
//   • Margins: None (we control bleed inline)
//   • Scale: 100%
//   • Background graphics: ON
//
// Route: /admin/print-stickers/:batchName  ?packs=1,2,5 (optional filter)
export default function StickerPrintSheet() {
  const { batchName } = useParams();
  const [params] = useSearchParams();
  const onlyPacks = (params.get("packs") || "")
    .split(",")
    .map((s) => parseInt(s.trim(), 10))
    .filter((n) => Number.isFinite(n) && n > 0);
  const [codes, setCodes] = useState([]);
  const [packSize, setPackSize] = useState(10);
  const [err, setErr] = useState("");

  // We stringify onlyPacks before passing into the dep array so the
  // exhaustive-deps lint rule can statically check it. Without this it
  // complains about a "complex expression" AND a missing dep.
  const onlyPacksKey = onlyPacks.join(",");
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/assets/batch/${encodeURIComponent(batchName)}`);
      let rows = data.codes || [];
      if (onlyPacks.length) rows = rows.filter((r) => onlyPacks.includes(r.pack_number));
      setCodes(rows);
      setPackSize(data.pack_size || 10);
    } catch (e) {
      setErr(formatApiError(e));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchName, onlyPacksKey]);

  useEffect(() => { load(); }, [load]);

  if (err) return <div className="p-10 text-center text-red-600">{err}</div>;
  if (!codes.length) return <div className="p-10 text-center label-tag">Loading sticker batch…</div>;

  // Group codes into packs so each pack gets its own A4 page.
  const packs = {};
  for (const c of codes) {
    const pn = c.pack_number || 1;
    if (!packs[pn]) packs[pn] = [];
    packs[pn].push(c);
  }
  const packNumbers = Object.keys(packs).map(Number).sort((a, b) => a - b);

  return (
    <div className="bg-white">
      {/* Print-only CSS — hides the toolbar and locks page size. */}
      <style>{`
        @page { size: A4 portrait; margin: 0; }
        @media print {
          .no-print { display: none !important; }
          html, body { margin: 0; padding: 0; background: white; }
        }
        .sticker-page {
          width: 210mm;
          height: 297mm;
          padding: 6mm;
          box-sizing: border-box;
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          grid-template-rows: repeat(5, 1fr);
          gap: 4mm;
          page-break-after: always;
        }
        .sticker-cell {
          border: 1.5px solid #000;
          display: grid;
          grid-template-columns: 38mm 1fr;
          align-items: center;
          padding: 3mm;
          background: #fff;
          position: relative;
          overflow: hidden;
        }
        .sticker-cell .qr {
          width: 32mm; height: 32mm;
          border: 1px solid #000;
          background: #fff;
          display: flex; align-items: center; justify-content: center;
        }
        .sticker-cell .meta { padding-left: 3mm; }
        .sticker-cell .brand {
          font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
          font-weight: 900;
          font-size: 7.5pt;
          letter-spacing: 1.5px;
          background: #000;
          color: #FFCC00;
          padding: 1.5mm 2mm;
          display: inline-block;
        }
        .sticker-cell .code {
          font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
          font-size: 10pt;
          font-weight: 700;
          margin-top: 1.5mm;
        }
        .sticker-cell .scan {
          font-family: ui-sans-serif, system-ui, sans-serif;
          font-size: 7pt;
          color: #444;
          margin-top: 1mm;
          line-height: 1.2;
        }
        .sticker-cell .bleed {
          position: absolute;
          right: -1px; top: -1px;
          background: #FFCC00;
          color: #000;
          font-family: ui-sans-serif, system-ui;
          font-weight: 900;
          font-size: 6.5pt;
          padding: 1mm 2mm;
          letter-spacing: 1px;
        }
        .page-header {
          font-family: ui-sans-serif, system-ui;
          font-size: 7pt;
          color: #666;
          letter-spacing: 1px;
          margin-bottom: 2mm;
        }
      `}</style>

      <div className="no-print p-4 sticky top-0 bg-white border-b-2 border-black flex items-center gap-3 z-10">
        <div>
          <div className="font-display font-black text-lg">Sticker print sheet</div>
          <div className="label-tag text-neutral-600 truncate">{batchName} · {codes.length} stickers · {packNumbers.length} page(s)</div>
          <div className="text-[11px] text-neutral-700 mt-1">
            <strong>To save as PDF:</strong> click Print → in the print dialog change <em>Destination</em> to <strong>"Save as PDF"</strong> → Save.
          </div>
        </div>
        <div className="ml-auto flex gap-2">
          <button onClick={() => window.print()} className="bg-black text-white label-tag px-4 py-2 brutal-btn" data-testid="print-stickers-btn">Print / Save PDF</button>
          <a href="/admin/dashboard" className="label-tag px-4 py-2 border-2 border-black brutal-btn">Back</a>
        </div>
      </div>

      {packNumbers.map((pn) => {
        const items = packs[pn].slice(0, packSize);
        return (
          <div key={pn} className="sticker-page" data-testid={`sticker-page-${pn}`}>
            {items.map((c, idx) => {
              const publicUrl = `${PUBLIC_FRONT_URL}/a/${c.asset_code}`;
              return (
                <div key={c.asset_code} className="sticker-cell" data-testid={`sticker-cell-${c.asset_code}`}>
                  <div className="bleed">PACK {pn} · {idx + 1}/{packSize}</div>
                  <div className="qr">
                    <QRCodeSVG value={publicUrl} size={108} level="M" includeMargin={false} />
                  </div>
                  <div className="meta">
                    <div className="brand">CURB CREW</div>
                    <div className="code">#{c.asset_code}</div>
                    <div className="scan">Scan to link this sticker to your card · report a fault</div>
                  </div>
                </div>
              );
            })}
            {/* Pad short packs so the grid stays 2×5. */}
            {Array.from({ length: Math.max(0, packSize - items.length) }).map((_, i) => (
              <div key={`pad-${i}`} className="sticker-cell" style={{ border: "1.5px dashed #ccc" }} />
            ))}
          </div>
        );
      })}
    </div>
  );
}
